import { Reveal, SectionTag, MaskedLines } from "@/components/Reveal";

/* illustrative market markers — subtle by design, no presence implied */
const REGIONS = [
  { name: "India", angle: -158 },
  { name: "UAE", angle: -124 },
  { name: "USA", angle: -90 },
  { name: "UK", angle: -56 },
  { name: "Canada", angle: -22 },
];

const R = 340;
const CX = 600;
const CY = 620;

const point = (deg) => {
  const a = (deg * Math.PI) / 180;
  return [CX + R * Math.cos(a), CY + R * Math.sin(a)];
};

export default function Global() {
  return (
    <section
      id="global"
      data-testid="global-outlook-section"
      className="relative scroll-mt-20 overflow-hidden bg-[#0A0906] py-20 sm:py-24"
    >
      <div
        className="pointer-events-none absolute left-1/2 bottom-[-30%] h-[60vh] w-[80vw] -translate-x-1/2 rounded-full bg-[#B8962E]/[0.08] blur-[140px]"
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <Reveal>
              <SectionTag index="11">Market Perspective</SectionTag>
            </Reveal>
            <h2
              data-testid="global-headline"
              className="mt-6 font-serif text-3xl font-normal leading-[1.12] tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
            >
              <MaskedLines delay={0.1}>
                Built for
                <br />
                <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
                  connected markets.
                </span>
              </MaskedLines>
            </h2>
          </div>
          <Reveal delay={0.2}>
            <div className="max-w-xl self-end space-y-5 text-base font-normal leading-relaxed text-slate-300 sm:text-lg lg:ml-auto">
              <p>
                The digital systems and business models we develop are shaped by
                increasingly connected customer expectations, service
                environments and technology ecosystems.
              </p>
              <p className="text-[15px] text-stone-400">
                Our approach is designed to remain relevant across different
                markets, customer journeys and operating requirements.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.25}>
          <div data-testid="global-visual" className="relative mt-10 sm:mt-6">
            <svg
              viewBox="0 0 1200 520"
              className="w-full"
              role="img"
              aria-label="Abstract connected-markets visual with illustrative regional markers"
            >
              {[180, 280, 400, 540].map((r) => (
                <circle
                  key={r}
                  cx={CX}
                  cy={CY}
                  r={r}
                  fill="none"
                  stroke="rgba(148,163,184,0.09)"
                  strokeWidth="1"
                />
              ))}
              <g className="lb-orbit" style={{ transformOrigin: "600px 620px" }}>
                <circle
                  cx={CX}
                  cy={CY}
                  r={R}
                  fill="none"
                  stroke="rgba(212,175,55,0.35)"
                  strokeWidth="1"
                  strokeDasharray="3 12"
                />
              </g>
              <line
                x1="80"
                y1={CY - 20}
                x2="1120"
                y2={CY - 20}
                stroke="rgba(212,175,55,0.15)"
                strokeWidth="1"
              />
              {REGIONS.map((r) => {
                const [x, y] = point(r.angle);
                return (
                  <g key={r.name}>
                    <circle cx={x} cy={y} r="9" fill="rgba(212,175,55,0.14)" />
                    <circle cx={x} cy={y} r="3" fill="#E7CE8B" opacity="0.75" />
                    <text
                      x={x}
                      y={y - 18}
                      textAnchor="middle"
                      fill="#78716C"
                      fontSize="13"
                      letterSpacing="2.5"
                      className="hidden uppercase sm:block"
                    >
                      {r.name}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-3 sm:hidden">
              {REGIONS.map((r) => (
                <span
                  key={r.name}
                  className="flex items-center gap-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-stone-500"
                >
                  <span className="h-1 w-1 rounded-full bg-[#E7CE8B]/70" aria-hidden="true" />
                  {r.name}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
